import type { RouterInputs, RouterOutputs } from 'shared/api'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useSelectedLocationStore } from './selected-location'

export type User = RouterOutputs['auth']['Login']['user']
export type UserInput = RouterInputs['auth']['Login']
export type { Role } from 'shared/lib/roles'

interface AuthState {
	user: User
	token: string | null
	isAuthenticated: boolean

	setAuth: (token: string, user: User) => void
	logout: () => void
}

const emptyUser: User = {
	_id: '',
	name: '',
	// The logged-out placeholder carries a least-privileged role (ADR-0029 — there
	// is no elevated bypass role). Real authority is the live staff record the
	// backend reads, never this client-side default.
	role: 'operator',
	username: '',
	locationId: null,
}

export const useAuthStore = create<AuthState>()(
	persist(
		(set) => ({
			user: emptyUser,
			token: null,
			isAuthenticated: false,

			setAuth: (token, user) =>
				set({
					token,
					user,
					isAuthenticated: true,
				}),

			logout: () => {
				set({
					user: emptyUser,
					token: null,
					isAuthenticated: false,
				})
				// The chosen Point belongs to the person who chose it. Leaving it set
				// would hand the next login someone else's shelf — and an `operator`
				// bound elsewhere would open onto a Point the backend then refuses.
				useSelectedLocationStore.getState().clearLocation()
			},
		}),
		{
			name: '@auth',
			partialize: (state) => ({
				user: state.user,
				token: state.token,
				isAuthenticated: state.isAuthenticated,
			}),
		}
	)
)
